import type { Assignment } from '@/types/domain';
import { DispatchSwitch } from '@/types/domain';

export type StopKind = 'pickup' | 'delivery';

/**
 * A "stop" is a group of bills (assignments) sharing the same physical address.
 * The driver works all bills at a stop together (one arrival, one signature).
 */
export interface Stop {
  key: string;
  kind: StopKind;
  name: string;
  address: string;
  city: string;
  bills: Assignment[];
}

const norm = (s?: string | null) => (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Dispatch routing (`DEV_f200_disp_switch`):
 *  - Pickup happens at the SHIPPER for 1/2, and at the DOCK for 4 (pick up from transfer).
 *  - Delivery happens at the CONSIGNEE for 1/4, and at the DOCK for 2 (deliver to transfer).
 */
export function isDockStop(kind: StopKind, a: Assignment): boolean {
  const d = (a.remDispatchType ?? '').trim();
  return kind === 'pickup'
    ? d === DispatchSwitch.DockToConsignee // 4 = pick up from dock
    : d === DispatchSwitch.ShipperToDock; // 2 = deliver to dock
}

/**
 * Group assignments into stops, filtered by the dock/non-dock routing tab.
 */
export function groupStops(
  assignments: Assignment[],
  kind: StopKind,
  dock: boolean,
): Stop[] {
  const filtered = assignments.filter((a) => isDockStop(kind, a) === dock);
  const map = new Map<string, Stop>();
  for (const a of filtered) {
    const address = (kind === 'pickup' ? a.remShipperAddress : a.remConsigneeAddress) ?? '';
    const city = (kind === 'pickup' ? a.remShipperCity : a.remConsigneeCity) ?? '';
    const name = (kind === 'pickup' ? a.remShipperName : a.remConsigneeName) ?? '';
    // Native groups by name|address|city so two different companies at the same
    // address are NOT merged into one stop (load_screen.dart:67).
    const key = `${norm(name)}|${norm(address)}|${norm(city)}` || a.remF04Id;
    const existing = map.get(key);
    if (existing) {
      existing.bills.push(a);
    } else {
      map.set(key, { key, kind, name, address, city, bills: [a] });
    }
  }
  return [...map.values()].sort((x, y) => x.name.localeCompare(y.name));
}

/**
 * Aggregate piece/weight totals and a de-duplicated reference string for a set
 * of bills at one stop. Mirrors the native list-card fold logic
 * (load_screen.dart:163, unload_screen.dart:163).
 */
export function aggregateBills(bills: Assignment[]): {
  pcs: number;
  wgt: number;
  refs: string;
} {
  const toInt = (v?: string | null) => (v ? parseInt(v, 10) || 0 : 0);
  const pcs = bills.reduce((sum, b) => sum + toInt(b.remPiecesSummary), 0);
  const wgt = bills.reduce((sum, b) => sum + toInt(b.remWeightSummary), 0);
  const refs = [
    ...new Set(
      bills
        .flatMap((b) => [b.remRef1, b.remRef2, b.remRef3, b.remRef4])
        .filter((r): r is string => !!r && r.trim().length > 0)
        .map((r) => r.trim()),
    ),
  ].join('  \u00B7  ');
  return { pcs, wgt, refs };
}

/**
 * Dispatch routing label shown on delivery (unload) cards, matching native:
 *  - disp 2 = Dock (S→T), disp 4 = T→C, otherwise S→C.
 */
export function dispatchLabel(a: Assignment): string {
  const d = (a.remDispatchType ?? '').trim();
  if (d === DispatchSwitch.ShipperToDock) return 'Dock (S\u2192T)';
  if (d === DispatchSwitch.DockToConsignee) return 'T\u2192C';
  return 'S\u2192C';
}

/** Encode/decode a stop key for use in a URL param. */
export const stopKey = {
  encode: (key: string) => encodeURIComponent(key),
  decode: (param: string) => decodeURIComponent(param),
};
